/*
 * Product page "As seen on you" (sections/product-seen-on-you.liquid).
 * Our own posts (metaobject entries, from the section's JSON) and Covet.pics posts, as 3:4 tiles:
 * own posts tagged with this product, Covet posts tagged with it, then our other posts and the Covet gallery to fill.
 * Videos load and play muted only while on screen. Clicking a tile opens a modal with the post
 * and the products tagged in it (live price from /products/<handle>.js).
 */
(function () {
  const api = 'https://app.covet.pics/api/v1';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getJson = (url) =>
    fetch(url)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

  function init(root) {
    if (root.dataset.seenReady) return;
    root.dataset.seenReady = 'true';

    const shop = root.dataset.shop;
    const productId = root.dataset.productId;
    const galleryId = (root.dataset.galleryId || '').trim();
    const max = parseInt(root.dataset.max, 10) || 12;
    const min = parseInt(root.dataset.min, 10) || 4;
    const showCovet = root.dataset.covet !== 'false';
    const fillOwn = root.dataset.fillOwn !== 'false';

    let own = [];
    try {
      own = JSON.parse(root.querySelector('[data-seen-own]').textContent) || [];
    } catch (error) {
      own = [];
    }

    const posts = [];
    const used = new Set();
    const add = (items) =>
      (items || []).forEach((item) => {
        if (posts.length >= max || used.has(item.post_id)) return;
        if (!coverUrl(item)) return;
        used.add(item.post_id);
        posts.push(item);
      });

    add(own.filter((item) => item.here));

    (showCovet ? getJson(`${api}/shop/${shop}/product/shopify/${productId}`) : Promise.resolve(null))
      .then((data) => {
        add(data && data.items);
        if (fillOwn) add(own.filter((item) => !item.here));
        if (posts.length >= max || !/^\d+$/.test(galleryId)) return;
        // Top up from the gallery, posts that have products tagged first so the modal has something to shop.
        return Promise.all([1, 2].map((page) => getJson(`${api}/embed/${galleryId}?page=${page}`))).then((pages) => {
          const items = pages.reduce((all, gallery) => all.concat((gallery && gallery.items) || []), []);
          add(items.filter((item) => productLinks(item).length));
          add(items);
        });
      })
      .then(() => {
        if (posts.length < min) return;
        const track = root.querySelector('[data-seen-track]');
        posts.forEach((post, index) => track.appendChild(tile(post, root, index)));
        root.hidden = false;
        setUpArrows(root, track);
        setUpModal(root, posts);
        if (root.dataset.autoplay === 'true' && !reduceMotion) setUpVideos(track);
      });
  }

  function coverUrl(post) {
    return (post.images && post.images.medium_resolution) || (post.cover_images && post.cover_images.medium_resolution) || '';
  }

  // Product links tagged on a post, as { handle, path, title, image, priceLabel }.
  function productLinks(post) {
    return (post.links || [])
      .map((link) => {
        try {
          const path = new URL(link.link, window.location.origin).pathname.replace(/\/$/, '');
          const match = path.match(/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/products\/([^/]+)$/i);
          if (!match) return null;
          return {
            handle: match[1],
            path,
            title: link.title || '',
            image: link.image || '',
            priceLabel: (link.prices && link.prices.price_label) || '',
          };
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);
  }

  function tile(post, root, index) {
    // Label with a tagged product other than this one; without JS the tile links there (or to the post).
    const here = root.dataset.productHandle;
    const product = productLinks(post).find((link) => link.handle !== here);

    const item = document.createElement('li');
    item.className = 'seen-on-you__item';

    const link = document.createElement('a');
    link.className = 'seen-on-you__tile';
    link.dataset.index = index;
    link.setAttribute('aria-haspopup', 'dialog');
    if (product) {
      link.href = product.path;
    } else {
      link.href = post.url || '#';
      link.target = '_blank';
      link.rel = 'noopener';
    }

    const alt = (post.images && post.images.alt_tag) || post.alt_tag || post.caption || '';
    const img = document.createElement('img');
    img.src = coverUrl(post);
    img.alt = alt.slice(0, 120);
    img.loading = 'lazy';
    link.appendChild(img);

    if (post.data_type === 'video' && post.video) {
      link.dataset.video = post.video;
      const badge = document.createElement('span');
      badge.className = 'seen-on-you__badge';
      badge.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 4l14 8-14 8z"/></svg>';
      link.appendChild(badge);
    }

    if (product || post.username) {
      const label = document.createElement('span');
      label.className = product ? 'seen-on-you__label' : 'seen-on-you__label seen-on-you__label--handle';
      label.textContent = product ? product.title : `@${post.username}`;
      link.appendChild(label);
    }

    item.appendChild(link);
    return item;
  }

  // Modal: the post (image, video or carousel) beside the products tagged in it.
  const productCache = {};
  const getProduct = (handle) => {
    if (!productCache[handle]) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productCache[handle] = getJson(`${root}products/${handle}.js`);
    }
    return productCache[handle];
  };

  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withCommas = (value, thousands, decimal) => {
      const [whole, fraction] = value.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction === undefined ? grouped : grouped + decimal + fraction;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      if (key === 'amount_no_decimals') return withCommas(Math.round(cents / 100).toString(), ',', '.');
      if (key === 'amount_with_comma_separator') return withCommas(amount, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return withCommas(Math.round(cents / 100).toString(), '.', ',');
      return withCommas(amount, ',', '.');
    });
  }

  function imageUrl(src, width) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = `https:${src}`;
    if (src.indexOf('cdn.shopify.com') === -1) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + `width=${width}`;
  }

  function setUpModal(root, posts) {
    const modal = root.querySelector('[data-seen-modal]');
    if (!modal) return;
    // Out of the section (overflow: hidden) so position: fixed covers the page. Drop a stale copy from an editor reload.
    const stale = document.body.querySelector(`:scope > #${modal.id}`);
    if (stale) stale.remove();
    document.body.appendChild(modal);

    const dialog = modal.querySelector('.seen-modal__dialog');
    const media = modal.querySelector('[data-seen-media]');
    const handleLink = modal.querySelector('[data-seen-handle]');
    const caption = modal.querySelector('[data-seen-caption]');
    const productsWrap = modal.querySelector('[data-seen-products]');
    const list = modal.querySelector('[data-seen-list]');
    const steps = modal.querySelectorAll('[data-seen-step]');
    const here = root.dataset.productHandle;
    const buttonLabel = root.dataset.buttonLabel || 'Shop now';

    let current = -1;
    let opener = null;
    let lockedOverflow = null;

    const show = (index) => {
      current = (index + posts.length) % posts.length;
      const post = posts[current];
      renderMedia(media, post);

      handleLink.textContent = post.username ? `@${post.username}` : '';
      handleLink.href = post.url || '#';
      handleLink.hidden = !post.url || !post.username;
      caption.textContent = post.caption || '';
      caption.hidden = !post.caption;

      const products = productLinks(post);
      list.innerHTML = '';
      productsWrap.hidden = !products.length;
      products.forEach((product) => list.appendChild(productCard(product, product.handle === here, buttonLabel, close)));
      steps.forEach((step) => (step.hidden = posts.length < 2));
    };

    const open = (index, trigger) => {
      opener = trigger;
      show(index);
      modal.hidden = false;
      lockedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => modal.classList.add('is-open'));
      dialog.focus({ preventScroll: true });
    };

    function close() {
      if (modal.hidden) return;
      modal.classList.remove('is-open');
      modal.hidden = true;
      media.innerHTML = '';
      document.body.style.overflow = lockedOverflow || '';
      if (opener) opener.focus({ preventScroll: true });
    }

    root.querySelector('[data-seen-track]').addEventListener('click', (event) => {
      const link = event.target.closest('.seen-on-you__tile');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      open(parseInt(link.dataset.index, 10) || 0, link);
    });

    modal.querySelectorAll('[data-seen-close]').forEach((button) => button.addEventListener('click', close));
    steps.forEach((step) => step.addEventListener('click', () => show(current + parseInt(step.dataset.seenStep, 10))));

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowLeft' && !event.target.closest('[data-seen-slides]')) {
        show(current - 1);
      } else if (event.key === 'ArrowRight' && !event.target.closest('[data-seen-slides]')) {
        show(current + 1);
      } else if (event.key === 'Tab') {
        // Keep focus inside the dialog.
        const focusable = Array.from(dialog.querySelectorAll('a[href], button, video[controls], [tabindex="0"]')).filter(
          (el) => !el.hidden && el.offsetParent !== null
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  }

  function renderMedia(media, post) {
    media.innerHTML = '';
    const alt = ((post.images && post.images.alt_tag) || post.alt_tag || post.caption || '').slice(0, 120);

    if (post.data_type === 'video' && post.video) {
      const video = document.createElement('video');
      video.src = post.video;
      video.poster = (post.cover_images && (post.cover_images.high_resolution || post.cover_images.medium_resolution)) || '';
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.controls = true;
      if (!reduceMotion) video.autoplay = true;
      media.appendChild(video);
      if (!reduceMotion) {
        const playing = video.play();
        if (playing) playing.catch(() => {});
      }
      return;
    }

    const slides = (post.carousel_media || []).filter((slide) => slide.standard_resolution);
    if (post.data_type === 'carousel' && slides.length > 1) {
      const track = document.createElement('div');
      track.className = 'seen-modal__slides';
      track.dataset.seenSlides = '';
      track.tabIndex = 0;
      track.setAttribute('aria-label', `${slides.length} photos`);
      slides.forEach((slide, i) => {
        const frame = document.createElement('div');
        frame.className = 'seen-modal__slide';
        if (slide.type === 'video' && slide.video) {
          const video = document.createElement('video');
          video.src = slide.video;
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          video.controls = true;
          frame.appendChild(video);
        } else {
          const img = document.createElement('img');
          img.src = slide.standard_resolution;
          img.alt = i === 0 ? alt : '';
          if (i > 0) img.loading = 'lazy';
          frame.appendChild(img);
        }
        track.appendChild(frame);
      });
      media.appendChild(track);

      const dots = document.createElement('div');
      dots.className = 'seen-modal__dots';
      dots.setAttribute('aria-hidden', 'true');
      slides.forEach(() => dots.appendChild(document.createElement('span')));
      media.appendChild(dots);
      const mark = () => {
        const at = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === at));
      };
      track.addEventListener('scroll', mark, { passive: true });
      mark();

      ['prev', 'next'].forEach((dir) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `seen-modal__slide-arrow seen-modal__slide-arrow--${dir}`;
        button.setAttribute('aria-label', dir === 'prev' ? 'Previous photo' : 'Next photo');
        button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' + (dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') + '"/></svg>';
        button.addEventListener('click', () =>
          track.scrollBy({ left: (dir === 'prev' ? -1 : 1) * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' })
        );
        media.appendChild(button);
      });
      return;
    }

    const img = document.createElement('img');
    img.src = (post.images && (post.images.high_resolution || post.images.medium_resolution)) || coverUrl(post);
    img.alt = alt;
    media.appendChild(img);
  }

  function productCard(product, isHere, buttonLabel, close) {
    const item = document.createElement('li');
    item.className = 'seen-modal__product';

    const imageLink = document.createElement('a');
    imageLink.className = 'seen-modal__product-image';
    imageLink.href = product.path;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    if (product.image) img.src = imageUrl(product.image, 240);
    imageLink.appendChild(img);

    const body = document.createElement('div');
    body.className = 'seen-modal__product-body';
    const title = document.createElement('a');
    title.className = 'seen-modal__product-title';
    title.href = product.path;
    title.textContent = product.title;
    const price = document.createElement('p');
    price.className = 'seen-modal__product-price';
    price.textContent = product.priceLabel.replace(/\s+[A-Z]{3}$/, '');
    const button = document.createElement('a');
    button.className = 'btn btn--primary btn--solid seen-modal__product-button';
    button.href = product.path;
    button.textContent = buttonLabel;
    body.append(title, price, button);
    item.append(imageLink, body);

    // Already on this product: close and go back up to the options instead of reloading.
    if (isHere) {
      [imageLink, title, button].forEach((el) =>
        el.addEventListener('click', (event) => {
          event.preventDefault();
          close();
          document.body.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
          window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        })
      );
    }

    // Live title, price and image from the store; Covet's copy can be stale. Hide products that are gone.
    getProduct(product.handle).then((data) => {
      if (!data) {
        item.hidden = true;
        return;
      }
      title.textContent = data.title;
      const from = data.price_varies ? 'From ' : '';
      price.textContent = from + formatMoney(data.price_min != null ? data.price_min : data.price);
      if (data.compare_at_price && data.compare_at_price > data.price && !data.price_varies) {
        const was = document.createElement('s');
        was.textContent = formatMoney(data.compare_at_price);
        price.append(' ', was);
      }
      if (data.featured_image) img.src = imageUrl(data.featured_image, 240);
      if (!data.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'seen-modal__product-soldout';
        soldOut.textContent = 'Sold out';
        price.append(' ', soldOut);
      }
    });

    return item;
  }

  function setUpVideos(track) {
    const tiles = track.querySelectorAll('[data-video]');
    if (!tiles.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const link = entry.target;
          let video = link.querySelector('video');
          if (entry.isIntersecting) {
            if (!video) {
              video = document.createElement('video');
              video.muted = true;
              video.loop = true;
              video.playsInline = true;
              video.setAttribute('playsinline', '');
              video.setAttribute('aria-hidden', 'true');
              video.preload = 'auto';
              video.poster = link.querySelector('img').src;
              video.src = link.dataset.video;
              link.insertBefore(video, link.querySelector('img').nextSibling);
            }
            const playing = video.play();
            if (playing) playing.catch(() => {});
          } else if (video) {
            video.pause();
          }
        });
      },
      { root: null, threshold: 0.6 }
    );
    tiles.forEach((link) => observer.observe(link));
  }

  function setUpArrows(root, track) {
    const prev = root.querySelector('[data-seen-prev]');
    const next = root.querySelector('[data-seen-next]');
    if (!prev || !next) return;

    const step = () => {
      const item = track.querySelector('.seen-on-you__item');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const perView = Math.max(1, Math.floor(track.clientWidth / (item.offsetWidth + gap)));
      return perView * (item.offsetWidth + gap);
    };

    // Phones have no arrows: a thin bar under the row shows it scrolls and how far along you are
    const viewport = track.closest('.seen-on-you__viewport') || track;
    let progress = root.querySelector('.slider-progress');
    if (!progress) {
      progress = document.createElement('div');
      progress.className = 'slider-progress';
      progress.setAttribute('aria-hidden', 'true');
      progress.innerHTML = '<span class="slider-progress__bar"></span>';
      viewport.after(progress);
    }
    const thumb = progress.firstChild;

    const update = () => {
      const scrollable = track.scrollWidth > track.clientWidth + 2;
      prev.hidden = next.hidden = progress.hidden = !scrollable;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (!scrollable) return;
      const share = track.clientWidth / track.scrollWidth;
      const at = Math.min(1, Math.max(0, track.scrollLeft / Math.max(1, track.scrollWidth - track.clientWidth)));
      thumb.style.width = `${share * 100}%`;
      thumb.style.transform = `translateX(${at * (1 / share - 1) * 100}%)`;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  const run = () => document.querySelectorAll('[data-seen-on-you]').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  // Theme editor: re-run when the section is added or re-rendered.
  document.addEventListener('shopify:section:load', run);
})();
